// THE TOTALS OVER EVERY MATCH (UI rebuild batch 2) — Astra's check "full-filter totals versus page totals", in its
// lightest form: one quiet link on the register's footer line opens this sheet. Per currency, the opening, the in, the
// out and the closing over the WHOLE filtered set, never the page (the route's totals; the order is today's — largest
// net first). Opening and closing are the accounts' balances at the window's edges: every desk account, or the one the
// account filter names. An older Worker sends no opening or closing: the net stands instead. The window caveat — the
// totals are short because an entry was reversed after the window — is the sheet's first line, by how much behind a tap.
import type { ReactNode } from 'react';
import { DetailSheet, Fact, StateLine } from '../kit';
import { Delta, Figure } from '../../Ledger';
import type { MovementTotal, MovementWindowCaveat } from '../../../lib/api';
import { displayAsset, fmtAmount } from '../../../lib/format';

export function TotalsSheet({ totals, caveat, onClose }: {
  totals: MovementTotal[];
  caveat: MovementWindowCaveat | null;
  onClose: () => void;
}) {
  const sorted = [...totals].sort((a, b) => Math.abs(b.net) - Math.abs(a.net) || a.asset.localeCompare(b.asset));
  const fig = (v: ReactNode) => <span className="font-mono tabular">{v}</span>;
  return (
    <DetailSheet title="Totals" onClose={onClose}>
      {caveat && (
        <StateLine
          tone="warn"
          className="mb-4"
          more={(
            <>
              {caveat.rows === 1 ? 'One entry here was' : `${caveat.rows} entries here were`}
              {' '}reversed after this window — totals are short by{' '}
              {caveat.per_asset.map((a) => `${fmtAmount(a.amount, a.asset)} ${displayAsset(a.asset)}`).join(', ')}.
              {' '}Drop the dates to tie exactly.
            </>
          )}
        >
          Totals are short — entries reversed after this window.
        </StateLine>
      )}
      <p className="mb-2 text-meta text-tg-hint">Every match, not just this page</p>
      {sorted.map((t) => (
        <section key={t.asset} className="mt-4">
          <h3 className="text-label font-medium uppercase tracking-wider text-tg-hint">{displayAsset(t.asset)}</h3>
          {t.opening != null && <Fact label="Opening">{fig(<Figure value={Number(t.opening)} asset={t.asset} />)}</Fact>}
          <Fact label="In">{fig(fmtAmount(t.in, t.asset))}</Fact>
          <Fact label="Out">{fig(fmtAmount(t.out, t.asset))}</Fact>
          {t.closing != null
            ? <Fact label="Closing">{fig(<Figure value={Number(t.closing)} asset={t.asset} />)}</Fact>
            : <Fact label="Net">{fig(<Delta value={t.net} asset={t.asset} />)}</Fact>}
        </section>
      ))}
    </DetailSheet>
  );
}
